import { isBefore } from "date-fns";
import { z } from "zod";

import { deliveryCostIssue, requiresDeliveryCost } from "../../utils/delivery-cost";
import type { DeliveryLocationOption } from "../DirectOfferDetails/schema";

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

const MAX_QUANTITY = 99999.99999;

// A single commodity line of a multi-commodity demand. Range/exact is dropped —
// demands are always exact, so only one quantity is captured per line and the
// adapter mirrors it into maximum_quantity.
const createItemSchema = (t: TranslateFn) => {
  const required = t("traderDetails.form.mandatoryFieldBlankError");

  const toNumber = (value: unknown) =>
    value === "" || value === undefined || value === null ? undefined : Number(value);

  return z.object({
    buy_commodity: z.number({ error: required }),
    quality: z.string({ error: required }).min(1, required),
    unit_of_measure: z.string({ error: required }).min(1, required),
    quantity: z.preprocess(
      toNumber,
      z
        .number({ error: required })
        .int(t("marketOfferDetails.form.mustBeInteger"))
        .positive(t("directOfferDetails.form.depositNonZero"))
        .max(MAX_QUANTITY, t("marketOfferDetails.form.quantityIsTooLarge"))
    ),
    price: z.preprocess(
      toNumber,
      z
        .number({ error: required })
        .int(t("marketOfferDetails.form.mustBeInteger"))
        .positive(t("directOfferDetails.form.priceNonZero"))
    ),
  });
};

export const createDemandSchema = (t: TranslateFn) => {
  const required = t("traderDetails.form.mandatoryFieldBlankError");

  return z
    .object({
      items: z
        .array(createItemSchema(t))
        .min(1, t("marketOfferDetails.form.itemsRequired"))
        .superRefine((items, ctx) => {
          const seen = new Map<number, number>();
          items.forEach((item, index) => {
            if (item.buy_commodity === undefined || item.buy_commodity === null) {
              return;
            }
            if (seen.has(item.buy_commodity)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [index, "buy_commodity"],
                message: t("marketOfferDetails.form.duplicateCommodity"),
              });
            } else {
              seen.set(item.buy_commodity, index);
            }
          });
        }),
      negotiable: z.boolean(),
      exchange_date: z.date({ error: required }).refine(date => !isBefore(date, new Date()), {
        message: t("marketOfferDetails.form.invalidExchangeDate"),
      }),
      delivery_location: z.custom<DeliveryLocationOption>(
        value => !!value && typeof value === "object" && "value" in value,
        { message: required }
      ),
      delivery_cost: z.string().optional(),
      comment: z.string().optional(),
      contract_number: z.string().optional(),
    })
    .superRefine(
      (values, ctx) => {
        // One delivery cost for the whole demand, whatever the number of commodity lines.
        if (!requiresDeliveryCost(values?.delivery_location?.value)) {
          return;
        }
        const issue = deliveryCostIssue(values.delivery_cost, t);
        if (issue) {
          ctx.addIssue({ code: "custom", path: ["delivery_cost"], message: issue });
        }
      },
      { when: () => true }
    );
};

export type DemandItemValues = {
  buy_commodity: number | null;
  quality: string;
  unit_of_measure: string;
  quantity: string;
  price: string;
};

export type DemandFormValues = {
  items: DemandItemValues[];
  negotiable: boolean;
  exchange_date: Date | null;
  delivery_location: DeliveryLocationOption | null;
  delivery_cost: string;
  comment?: string;
  contract_number?: string;
};

// The subset validated on step 1 (Continue) before revealing matching deposits.
export const STEP_ONE_FIELDS: (keyof DemandFormValues)[] = ["items"];
